import { motion } from 'framer-motion';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import CTASection from '../components/ui/CTASection';
import PageHero from '../components/ui/PageHero';
import { coreValues, whyChooseUs } from '../data/content';

const fadeUp = { initial: { opacity: 0, y: 25 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const expertise = [
  { title: 'Statutory Compliances', desc: 'Expert statutory compliance services including audit, taxation, and financial advisory.' },
  { title: 'Corporate Law', desc: 'Company formation, ROC compliance, and corporate legal support.' },
  { title: 'Intellectual Property', desc: 'Trademark, copyright, and patent assistance for brand protection.' },
  { title: 'Business Certifications', desc: 'ISO, MSME, FSSAI, and all essential business licenses.' },
  { title: 'NGO & Non-Profit', desc: 'Specialized registration and compliance for non-profit organizations.' },
  { title: 'Technology Solutions', desc: 'Web development, UI/UX design, and custom software solutions.' },
];



export default function AboutPage() {
  return (
    <main className="w-full overflow-x-hidden pt-16 lg:pt-20">
      {/* Hero */}
      <PageHero
        badge="About Us"
        title="About Finegal Tech Solution"
        subtitle="A premier multi-disciplinary consultancy with over 10 years of experience serving businesses across Finance, Legal, Compliance, and Technology."
      />

      {/* Firm Overview */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <SectionHeading title="Firm Overview" centered={false} />
              <p className="mb-4 text-base lg:text-lg" style={{ color: 'var(--color-text-muted)' }}>
                Finegal Tech Solution was founded with a clear vision: to make compliance, legal protection, and technology accessible to every business in India. We understand that navigating GST, company law, intellectual property, and digital presence simultaneously can be overwhelming.
              </p>
              <p className="mb-4 text-base lg:text-lg" style={{ color: 'var(--color-text-muted)' }}>
                That's why we bring together a team of qualified Chartered Accountants, Company Secretaries, Legal Professionals, and Technology Experts under one roof — to deliver integrated, end-to-end solutions that save you time, money, and stress.
              </p>
              <p className="text-base lg:text-lg" style={{ color: 'var(--color-text-muted)' }}>
                With 5000+ clients served across India and a 99% satisfaction rate, we've built our reputation on reliability, transparency, and results.
              </p>
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { n: '10+', l: 'Years Experience' },
                { n: '5000+', l: 'Clients Served' },
                { n: '50+', l: 'Expert Team Members' },
                { n: '200+', l: 'Services Offered' },
              ].map((s, i) => (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card p-6 text-center"
                  style={{ background: i % 2 === 0 ? 'var(--gradient-primary)' : 'var(--color-surface)' }}
                >
                  <div className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: i % 2 === 0 ? 'var(--color-secondary-light)' : 'var(--color-primary)' }}>{s.n}</div>
                  <div className="text-sm lg:text-base" style={{ color: i % 2 === 0 ? 'rgba(255,255,255,0.8)' : 'var(--color-text-muted)' }}>{s.l}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Mission & Vision */}
      <section className="section-pad section-bg-alt">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                badge: 'Our Mission',
                title: 'Empowering Business Growth',
                text: 'To simplify compliance, legal protection, and technology adoption for Indian businesses — enabling entrepreneurs to focus on growth while we handle the complexity. We are committed to delivering accurate, timely, and cost-effective solutions that add real value.',
                icon: '🎯',
              },
              {
                badge: 'Our Vision',
                title: 'India\'s Most Trusted Consultancy',
                text: 'To become India\'s most trusted one-stop business consultancy by providing seamless, integrated services in Finance, Legal, Compliance, and Technology — empowering millions of businesses to thrive in a complex regulatory environment.',
                icon: '🔭',
              },
            ].map((item, i) => (
              <motion.div
                key={item.badge}
                {...fadeUp}
                transition={{ delay: i * 0.15 }}
                className="card p-8"
              >
                <div className="text-4xl lg:text-5xl mb-4">{item.icon}</div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ background: 'var(--gradient-gold)', color: 'var(--color-primary-dark)' }}>
                  {item.badge}
                </span>
                <h3 className="text-xl lg:text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>{item.title}</h3>
                <p className="text-sm lg:text-base leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Core Values */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading badge="Our Values" title="Core Values That Drive Us" subtitle="The principles that guide every decision, every interaction, and every service we deliver." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-6 border-t-4"
                style={{ borderTopColor: 'var(--color-secondary)' }}
              >
                <h3 className="font-bold text-lg lg:text-xl mb-2" style={{ color: 'var(--color-text)' }}>{v.title}</h3>
                <p className="text-sm lg:text-base" style={{ color: 'var(--color-text-muted)' }}>{v.description}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Why Finegal */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading badge="Why Finegal" title="Why Choose Finegal Tech Solution" subtitle="What makes us different from other consultancies." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-6 flex gap-4"
              >
                <span className="text-3xl lg:text-4xl flex-shrink-0">{item.icon}</span>
                <div>
                  <h3 className="font-bold lg:text-lg mb-1" style={{ color: 'var(--color-text)' }}>{item.title}</h3>
                  <p className="text-sm lg:text-base" style={{ color: 'var(--color-text-muted)' }}>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Our Expertise */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading badge="Our Expertise" title="Areas of Specialization" subtitle="Deep expertise across six key domains to serve all your business needs." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertise.map((e, i) => (
              <motion.div
                key={e.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-6"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 font-bold text-sm text-white" style={{ background: 'var(--gradient-primary)' }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="font-bold lg:text-lg mb-2" style={{ color: 'var(--color-text)' }}>{e.title}</h3>
                <p className="text-sm lg:text-base" style={{ color: 'var(--color-text-muted)' }}>{e.desc}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <CTASection title="Ready to Work With Us?" subtitle="Schedule a free consultation with our experts today." />
    </main>
  );
}
